import "./dashboard.css";
import { GroupCodeCard } from "./GroupCodeCard";
import { ClassRenameModal } from "./ClassRenameModal";
import { LessonGroupsList } from "./LessonGroupsList";
import { ClassroomAssignmentView } from "./ClassroomAssignmentView";
import { GroupsEmptyState } from "./GroupsEmptyState";
import { WarmUpEmptyStates } from "./WarmUpEmptyStates";
import { WarmUpGroupsLoadError } from "./WarmUpGroupsLoadError";
import { warmUpPickerUrl } from "./warmUpEntry";
import { GroupsSkeleton } from "./GroupsSkeleton";
import { QuickGroupsSection } from "./QuickGroupsSection";
import { useClassroomQuickGroups } from "hooks/useQuickGroups/useQuickGroups";
import { useClassroomWarmUpGroups } from "hooks/useWarmUpGroups/useWarmUpGroups";
import { useLastFocus } from "hooks/useLastFocus/useLastFocus";
import { focusPickerParams } from "./assignmentDraft";
import { DEFAULT_LIST_SETTINGS } from "./oneListCards";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { classDisplayName } from "components/shared/ClassroomFilter/groupClassesBySchoolYear";
import { request } from "hooks/useOkoApi/useOkoApi";
import { useStandards, usePreloadStandards } from "hooks/useStandards/useStandards";
import { useClassroomLessonAssignment } from "hooks/useCurriculum/useCurriculum";
import { Alert } from "react-bootstrap";
import { colors, constants, logEducatorDashboard, LogLevel } from "utils";
import IconInfo from "../../../../assets/Icon-Info.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { useAuthUserData } from "hooks/useAuthUserData/useAuthUserData";
import { useHistory, useLocation } from "react-router-dom";
import { EducatorGettingStartedModal } from "../EducatorGettingStartedModal";
import Question from "assets/question.svg";
import { ClassroomFilter } from "components/shared/ClassroomFilter/ClassroomFilter";
import { classesInActiveSchool } from "hooks/useEducatorAccount/useEducatorAccount";
import { LessonCopiedBanner, ToastStack } from "./CopyLessonModal";
import { trackClickedButton } from "utils/analytics";
import { useFeatureFlagStatus } from "hooks/useFeatureFlag";
import { manageGroupsBackground, useReportWarmSurface } from "./ManageGroupsMain";

const CLASS_STORAGE_KEY = "oko_selected_class_id";
const readStoredClassId = () => {
  try { return window.localStorage.getItem(CLASS_STORAGE_KEY); } catch { return null; }
};
const writeStoredClassId = (id) => {
  try { if (id) window.localStorage.setItem(CLASS_STORAGE_KEY, id); } catch { /* ignore */ }
};

export const GroupCodeView = () => {
  // showMessage prop controls Alert visibility
  // Can't use regular dismissible Alert here as there is a custom button in the design
  const [showMessage, setShowMessage] = useState(true);
  const { authUserData, loading } = useAuthUserData();
  const history = useHistory();
  const location = useLocation();
  const warmUpChallenges = useFeatureFlagStatus("warmUpChallenges");
  const standardsData = useStandards();

  // Read classId from URL params
  const urlParams = new URLSearchParams(location.search);
  const classIdFromUrl = urlParams.get("classId");

  // Preload standards with counts in the background for smoother navigation
  // This doesn't block the UI or show a spinner
  usePreloadStandards(true);

  const [codesToConfig, setCodesToConfig] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState();
  const [isEducatorGettingStartedModalOpen, setIsEducatorGettingStartedModalOpen] = useState(false);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [isClassRenameModalOpen, setIsClassRenameModalOpen] = useState(false);
  // One banner per class a lesson was copied into (Figma 5059-64109); each is
  // individually dismissible and its View link jumps to that class.
  const [copyBanners, setCopyBanners] = useState([]);
  // Set when the class's last warm-up card is archived: focus then goes to the
  // Create Groups button, or to the empty state's title if nothing is left.
  const [focusAfterLastWarmUp, setFocusAfterLastWarmUp] = useState(false);
  const clearFocusAfterLastWarmUp = useCallback(() => setFocusAfterLastWarmUp(false), []);
  // The one list's Sort By and Group Type: page settings, kept across classes.
  const [listSettings, setListSettings] = useState(DEFAULT_LIST_SETTINGS);

  // Cached so a return navigation paints instantly, stale immediately so every
  // mount still revalidates (classes and free-code counts change on other
  // pages). The full-screen spinner keys on the FIRST load only (OKO2-3684).
  const {
    data: educatorAccountData,
    isInitialLoading: accountLoading,
    error: accountError,
    refetch: refetchAccount,
  } = useQuery({
    queryKey: ["educator-account"],
    queryFn: async () => {
      try {
        const educatorData = await request("account/educator", { method: "GET" });
        logEducatorDashboard("Educator dashboard: Educator account loaded", {
          event: "account_loaded",
          classCount: educatorData?.classes?.length ?? 0,
          curriculumId: educatorData?.config?.curriculum_id ?? null,
          curriculumKind: educatorData?.config?.curriculum_kind ?? null,
        });
        return educatorData;
      } catch (e) {
        logEducatorDashboard("Educator dashboard: Failed to load educator data", {
          level: LogLevel.WARNING,
          event: "load_error",
          statusCode: e?.status,
          serverResponse: e?.detail,
          endpoint: "account/educator",
        });
        throw e;
      }
    },
    staleTime: 0,
  });

  const curriculumId = educatorAccountData?.config?.curriculum_id || null;
  const curriculumKind = educatorAccountData?.config?.curriculum_kind || null;
  const isCcssDifferentiation = curriculumKind === "CCSS_DIFFERENTIATION";
  const {
    assignment: lessonAssignment,
    loading: envelopeLoading,
    refetch: refetchAssignment,
    updatedAt: envelopeUpdatedAt,
  } = useClassroomLessonAssignment(
    curriculumId && selectedClassId && !isCcssDifferentiation ? selectedClassId : null
  );

  // Seed the class selector once the account is known: URL wins, then the
  // stored last selection, then the first visible class.
  useEffect(() => {
    if (!educatorAccountData || selectedClassId !== null) return;
    const visible = classesInActiveSchool(educatorAccountData);
    if (visible.length === 0) return;
    const validIds = new Set(visible.map((c) => c.id));
    const fromUrl = classIdFromUrl && validIds.has(classIdFromUrl) ? classIdFromUrl : null;
    const fromStorage = readStoredClassId();
    const fromStorageValid = fromStorage && validIds.has(fromStorage) ? fromStorage : null;
    setSelectedClassId(fromUrl || fromStorageValid || visible[0].id);
  }, [educatorAccountData, selectedClassId, classIdFromUrl]);

  useEffect(() => {
    if (window.localStorage.getItem("isDismissEducatorGettingStartedModal") !== "true") {
      logEducatorDashboard("Educator dashboard: Getting Started modal opened on initial load", {
        event: "getting_started_shown",
        trigger: "initial",
      });
      setIsEducatorGettingStartedModalOpen(true);
    }
  }, []);

  // Only the active school's classes reach the selector and everything keyed off it.
  const visibleClasses = classesInActiveSchool(educatorAccountData);
  const hasClasses = visibleClasses.length > 0;
  const selectedClass = hasClasses
    ? visibleClasses.find(c => c.id === selectedClassId)
    : null;
  const selectedClassCodes = selectedClass?.codes || [];

  // The lesson-picker endpoint returns a collection of lessons for the class.
  // Memoized so the empty fallback doesn't hand out a new array every render.
  const lessons = useMemo(() => lessonAssignment?.lessons || [], [lessonAssignment]);
  const lessonUnassignedCodes = lessonAssignment?.unassigned_codes || [];
  const hasLessons = lessons.length > 0;

  // Quick groups render in every class type and gate the "no groups" empty
  // state — a class with quick groups but no lessons/bundles is not empty.
  const {
    quickGroups,
    loading: quickGroupsLoading,
    refetch: refetchQuickGroups,
    updatedAt: quickGroupsUpdatedAt,
  } = useClassroomQuickGroups(selectedClassId);
  const hasQuickGroups = quickGroups.length > 0;

  // Only the lesson and CCSS dashboards show warm-up groups, and only once the
  // flag is known to be on; the legacy group cards never ask for them.
  const readsWarmUps = warmUpChallenges.enabled && !warmUpChallenges.isLoading
    && Boolean(curriculumId || isCcssDifferentiation);
  const {
    warmUpAssignments,
    loading: warmUpLoading,
    error: warmUpError,
    refetch: refetchWarmUps,
    replaceAssignment: replaceWarmUpAssignment,
    updatedAt: warmUpsUpdatedAt,
  } = useClassroomWarmUpGroups(selectedClassId, readsWarmUps);
  const hasWarmUps = warmUpAssignments.length > 0;
  // Changes whenever a source answers a read: the one list re-sorts then.
  const sourcesReadAt = `${envelopeUpdatedAt}|${quickGroupsUpdatedAt}|${warmUpsUpdatedAt}`;

  // Until the envelope and quick groups have loaded once, the class only LOOKS
  // empty — rendering the empty state then is the flash OKO2-3684 reports.
  // Disabled queries report loading=false, so this is safe in every flow.
  const groupsSettled = !envelopeLoading && !quickGroupsLoading && !warmUpLoading;

  // Quick groups count as much as lessons here. With neither left there's nothing
  // to derive, so this reads the stored record and the create flows keep the
  // class's last focus.
  const lastFocusEntries = useMemo(() => [...lessons, ...quickGroups], [lessons, quickGroups]);
  const lastFocus = useLastFocus(selectedClassId, lastFocusEntries);

  // Curriculum (lesson) flow renders the unified LessonGroupsList — shared top
  // bar + quick groups first, then lessons — whenever the class has either.
  // When it does, the standalone top ClassroomFilter / QuickGroupsSection are
  // suppressed (LessonGroupsList owns both).
  const isLessonFlow = Boolean(curriculumId && selectedClassId && !isCcssDifferentiation);
  // The pages that read warm-up groups are the flag-on surfaces (#FAF7F2); the
  // legacy group cards, and every page while the flag is unknown, off or
  // failed, keep today's background.
  const warmSurface = readsWarmUps;
  useReportWarmSurface(warmSurface);
  const showsUnifiedLessonList = isLessonFlow && (hasLessons || hasQuickGroups || hasWarmUps);

  useEffect(() => {
    // Only the legacy group-cards branch reads codesToConfig — the lesson and
    // CCSS flows were paying this POST (and its full-screen spinner) for data
    // they never render.
    if (curriculumId || isCcssDifferentiation) return;
    if (selectedClassCodes.length > 0) {
      async function fetchGrades() {
        setIsLoading(true);
        try {
          const data = await request("group/code_configurations", {
            method: "POST",
            payload: {
              codes: selectedClassCodes,
            },
          });
          const codesMap = {};
          data.configurations.forEach(({ code, config }) => {
            codesMap[code] = config;
          });
          setCodesToConfig(codesMap);
          setIsLoading(false);
        } catch (e) {
          setIsLoading(false);
          logEducatorDashboard("Educator dashboard: Failed to fetch group configurations", {
            level: LogLevel.WARNING,
            event: "fetch_configurations_error",
            statusCode: e?.status,
            serverResponse: e?.detail,
            endpoint: "group/code_configurations",
          });
          setError(e?.detail || e?.message || "An unknown error occurred.");
        }
      }
      fetchGrades();
    }
  }, [selectedClassId, educatorAccountData]);

  // Diagnostic: log which Groups branch renders and the values that decide it,
  // including the lesson-collection shape so a mismatch (e.g. lessons present but
  // not rendered) stays traceable in Sentry logs.
  useEffect(() => {
    if (loading) return;
    const branch =
      isCcssDifferentiation && selectedClassId ? "classroom_assignment_view"
        : curriculumId && selectedClassId
          ? (hasLessons ? "lesson_groups_list" : "no_lesson_empty_state")
          : "group_cards";
    const diag = {
      event: "groups_render_decision",
      branch,
      classroomId: selectedClassId,
      hasClasses,
      classCount: visibleClasses.length,
      curriculumId,
      curriculumKind,
      isCcssDifferentiation,
      lessonsCount: lessons.length,
      unassignedCodesCount: lessonUnassignedCodes.length,
      selectedClassCodesCount: selectedClassCodes.length,
    };
    logEducatorDashboard("Educator dashboard: Groups render decision", diag);
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn("[Groups] render decision →", branch, diag);
    }
  }, [loading, isCcssDifferentiation, curriculumId, curriculumKind, selectedClassId,
    hasLessons, lessons.length, lessonUnassignedCodes.length, hasClasses,
    educatorAccountData, visibleClasses.length, selectedClassCodes.length]);

  const triggerRefresh = async (code) => {
    setIsLoading(true);
    const newCodesToConfig = JSON.parse(JSON.stringify(codesToConfig));
    try {
      const data = await request(`group/${code}`, {
        method: "GET",
      });
      newCodesToConfig[code] = data.config;
      setCodesToConfig(newCodesToConfig);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      logEducatorDashboard("Educator dashboard: Failed to refresh group code", {
        level: LogLevel.WARNING,
        event: "refresh_code_error",
        statusCode: e?.status,
        serverResponse: e?.detail,
        endpoint: `group/${code}`,
      });
      setError(e?.detail || e?.message || "An unknown error occurred.");
    }
  }

  const triggerRefreshAll = async () => {
    if (selectedClassCodes.length === 0) {
      return;
    }

    setIsLoading(true);
    try {
      const data = await request("group/code_configurations", {
        method: "POST",
        payload: {
          codes: selectedClassCodes,
        },
      });
      const codesMap = {};
      data.configurations.forEach(({ code, config }) => {
        codesMap[code] = config;
      });
      setCodesToConfig(codesMap);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      logEducatorDashboard("Educator dashboard: Failed to refresh all group codes", {
        level: LogLevel.WARNING,
        event: "refresh_all_codes_error",
        statusCode: e?.status,
        serverResponse: e?.detail,
        endpoint: "group/code_configurations",
      });
      setError(e?.detail || e?.message || "An unknown error occurred.");
    }
  }

  const handleQuestionClick = () => {
    logEducatorDashboard("Educator dashboard: Getting Started modal opened via help icon", {
      event: "getting_started_shown",
      trigger: "help_icon",
    });
    setIsEducatorGettingStartedModalOpen(true);
  };

  const handleClassRename = async (classId, newName) => {
    const oldName = visibleClasses.find(c => c.id === classId)?.name;
    try {
      setIsLoading(true);
      await request(`account/classes/${classId}?name=${encodeURIComponent(newName)}`, {
        method: "PUT",
      });
      logEducatorDashboard("Educator dashboard: Classroom renamed", {
        event: "classroom_renamed",
        classroomId: classId,
        oldName,
        newName,
      });
      await refetchAccount();
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      logEducatorDashboard("Educator dashboard: Failed to rename classroom", {
        level: LogLevel.WARNING,
        event: "rename_error",
        classroomId: classId,
        statusCode: e?.status,
        serverResponse: e?.detail,
        endpoint: `account/classes/${classId}`,
      });
      setError(e?.detail || e?.message || "An unknown error occurred.");
    }
  };

  // After an all-or-nothing copy: queue a banner per target, then refresh the
  // account (target classes' free-code counts changed) and the current class's
  // envelope (the source can be its own copy target).
  const handleLessonCopied = (copies, lessonName) => {
    setCopyBanners((prev) => [
      ...prev,
      ...copies.map((c) => ({
        key: c.assignment_id,
        classroomId: c.classroom_id,
        message: `${lessonName} was copied in ${c.classroom_name}`,
      })),
    ]);
    refetchAccount();
    refetchAssignment();
  };

  const dismissCopyBanner = (key) => setCopyBanners((prev) => prev.filter((b) => b.key !== key));

  const handleClassChange = (classId) => {
    const selectedClassName = classDisplayName(visibleClasses.find(c => c.id === classId));
    logEducatorDashboard("Educator dashboard: Classroom filter changed", {
      event: "classroom_filter_changed",
      classroomId: classId,
      classroomName: selectedClassName,
    });
    setSelectedClassId(classId);
    setFocusAfterLastWarmUp(false);
    writeStoredClassId(classId);
    const params = new URLSearchParams(location.search);
    params.set("classId", classId);
    const queryString = params.toString();
    history.replace(queryString ? `${location.pathname}?${queryString}` : location.pathname);
  };

  const handleEmptyClassLessonGroups = () => {
    // Same button_name as the lesson list's own entry point, despite the
    // label, so the create funnel has one first step for every class.
    trackClickedButton("Assign New Lesson", {
      page_name: "Dashboard",
      class: selectedClassId,
      source: "group_code_view_empty_state",
    });
    // No lessons assigned yet, so every classroom code is available
    // to distribute to the first lesson.
    const params = new URLSearchParams({
      classroomId: selectedClassId,
      totalAvailable: String(selectedClassCodes.length),
      ...focusPickerParams(lastFocus),
    });
    history.push(`${constants.LESSONS_STANDARDS_PATH}?${params}`);
  };

  const handleEmptyClassQuickGroup = () => {
    const params = new URLSearchParams({
      classroomId: selectedClassId,
      classId: selectedClassId,
      ...(lastFocus?.grade ? { grade: lastFocus.grade } : {}),
    });
    history.push(`${constants.CREATE_QUICK_GROUP_PATH}?${params}`);
  };

  const handleCreateWarmUpGroups = () => {
    trackClickedButton("Create Warm-Up Groups", {
      page_name: "Dashboard",
      class: selectedClassId,
      source: "group_code_view_empty_state",
    });
    history.push(warmUpPickerUrl(selectedClassId));
  };

  const handleSeeAllGroupTypes = () => {
    trackClickedButton("See All Group Types", {
      page_name: "Dashboard",
      class: selectedClassId,
      source: "group_code_view_empty_state",
    });
  };

  const handlePickWarmUp = () => {
    trackClickedButton("Pick a Warm-Up Challenge", {
      page_name: "Dashboard",
      class: selectedClassId,
      source: "group_code_view_empty_state",
    });
    history.push(warmUpPickerUrl(selectedClassId));
  };

  if (loading || !authUserData) return <></>;
  return (
    <div style={{
      display: "flex",
      backgroundColor: manageGroupsBackground(warmSurface),
      flexDirection: "column",
      gap: "24px",
      opacity: isLoading ? 0.5 : 1,
      padding: "0px 24px"
    }}>
      {copyBanners.length > 0 && (
        <ToastStack role="status" aria-live="polite">
          {copyBanners.map((b) => (
            <LessonCopiedBanner
              key={b.key}
              message={b.message}
              onView={() => {
                handleClassChange(b.classroomId);
                dismissCopyBanner(b.key);
              }}
              onClose={() => dismissCopyBanner(b.key)}
            />
          ))}
        </ToastStack>
      )}
      {/* Class Selector — hidden for CCSS_DIFFERENTIATION (ClassroomAssignmentView renders its own header row) and for the lesson-picker list (LessonGroupsList renders the shared Class + Sort By row). Kept for the no-lessons empty state. */}
      {hasClasses
        && !(isCcssDifferentiation && selectedClassId)
        && !showsUnifiedLessonList && (
        <ClassroomFilter
          classes={visibleClasses}
          selectedClassId={selectedClassId}
          onClassChange={handleClassChange}
          onEditClick={() => setIsClassRenameModalOpen(true)}
          variant={curriculumId ? "stacked" : undefined}
        />
      )}
      {/* Standalone Quick Groups only for the plain group-cards view. The
          curriculum (LessonGroupsList) and CCSS (ClassroomAssignmentView) views
          render Quick Groups internally, under their own Class + Create bar. */}
      {selectedClassId && !showsUnifiedLessonList && !isCcssDifferentiation && groupsSettled && (
        <QuickGroupsSection
          quickGroups={quickGroups}
          classroomId={selectedClassId}
          onChanged={refetchQuickGroups}
        />
      )}
      {accountLoading ? (
        // First-ever account load: the page's shape instead of the old
        // full-screen spinner. Every later visit serves the cached account.
        <GroupsSkeleton />
      ) : isCcssDifferentiation && selectedClassId ? (
        <ClassroomAssignmentView
          classroomId={selectedClassId}
          classes={visibleClasses}
          selectedClassId={selectedClassId}
          onClassChange={handleClassChange}
          onEditClass={() => setIsClassRenameModalOpen(true)}
          quickGroups={quickGroups}
          onQuickGroupsChanged={refetchQuickGroups}
          warmUpAssignments={warmUpAssignments}
          warmUpLoading={warmUpLoading}
          warmUpError={warmUpError}
          refetchWarmUps={refetchWarmUps}
          replaceWarmUpAssignment={replaceWarmUpAssignment}
          onLessonCopied={handleLessonCopied}
          onAccountRefresh={refetchAccount}
          totalCodes={selectedClassCodes.length}
          sourcesSettled={!quickGroupsLoading && !warmUpLoading}
          listSettings={listSettings}
          onListSettingsChange={setListSettings}
          sourcesReadAt={sourcesReadAt}
        />
      ) : curriculumId && selectedClassId ? (
        showsUnifiedLessonList ? (
          <LessonGroupsList
            lessons={lessons}
            quickGroups={quickGroups}
            warmUpAssignments={warmUpAssignments}
            warmUpError={warmUpError}
            refetchWarmUps={refetchWarmUps}
            replaceWarmUpAssignment={replaceWarmUpAssignment}
            onLastWarmUpArchived={() => setFocusAfterLastWarmUp(true)}
            focusCreateGroups={focusAfterLastWarmUp && !hasWarmUps}
            focusCreateGroupsInList={focusAfterLastWarmUp}
            onCreateGroupsFocused={clearFocusAfterLastWarmUp}
            onQuickGroupsChanged={refetchQuickGroups}
            unassignedCodes={lessonUnassignedCodes}
            classroomId={selectedClassId}
            curriculumId={curriculumId}
            totalCodes={selectedClassCodes.length}
            classes={visibleClasses}
            selectedClassId={selectedClassId}
            onClassChange={handleClassChange}
            onEditClass={() => setIsClassRenameModalOpen(true)}
            onRefresh={refetchAssignment}
            onLessonCopied={handleLessonCopied}
            onAccountRefresh={refetchAccount}
            sourcesSettled={groupsSettled}
            listSettings={listSettings}
            onListSettingsChange={setListSettings}
            sourcesReadAt={sourcesReadAt}
          />
        ) : !groupsSettled || warmUpChallenges.isLoading ? (
          // First load for this class still in flight — saying "no groups yet"
          // now would flash the empty state at a class that has groups. The
          // class filter above stays live; the groups area shows its shape.
          // Same while warm_up_challenges is unknown: either empty state now
          // could be the wrong one.
          <GroupsSkeleton withHeader={false} />
        ) : warmUpChallenges.enabled ? (
          <>
          {warmUpError && <WarmUpGroupsLoadError onRetry={refetchWarmUps} />}
          <WarmUpEmptyStates
            key={selectedClassId}
            style={{ marginTop: "8px" }}
            onPickWarmUp={handlePickWarmUp}
            onCreateLessonGroups={handleEmptyClassLessonGroups}
            onCreateQuickGroup={handleEmptyClassQuickGroup}
            onCreateWarmUpGroups={handleCreateWarmUpGroups}
            onSeeAllGroupTypes={handleSeeAllGroupTypes}
            focusTitle={focusAfterLastWarmUp}
            onTitleFocused={clearFocusAfterLastWarmUp}
          />
          </>
        ) : (
          <GroupsEmptyState
            // +8px on top of the container's 24px gap = 32px from the Class
            // filter to the icon, per Figma.
            style={{ marginTop: "8px" }}
            primaryLabel="Create Lesson Groups"
            onPrimary={handleEmptyClassLessonGroups}
            onQuickGroup={handleEmptyClassQuickGroup}
          />
        )
      ) : (
        <>
          <div style={{
            alignItems: "center",
            backgroundColor: "white",
            borderRadius: "12px",
            boxShadow: "0px 1px 2px 0px rgba(14, 26, 49, 0.05)"
          }}>
            <div>
              {showMessage && <Alert style={{ marginBottom: 0 }}
                variant="">
                <div style={{ display: "flex", gap: "16px" }}>
                  <div style={{
                    alignItems: "center",
                    color: colors.nightlyWoods500,
                    display: "flex",
                    minHeight: "36px",
                    gap: "12px",
                    flex: "1 0 0",
                    paddingRight: "28px"
                  }}>
                    <img src={IconInfo} alt="info" />
                    {constants.GROUP_CARD_VIEW_MESSAGE}
                    <button className="group-code-view-message-alert-close-button" aria-label="Close" onClick={() => setShowMessage(false)}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="25" height="24" viewBox="0 0 25 24" fill="none">
                        <path d="M19.847 18.2193C19.9166 18.289 19.9719 18.3717 20.0096 18.4628C20.0473 18.5538 20.0668 18.6514 20.0668 18.7499C20.0668 18.8485 20.0473 18.9461 20.0096 19.0371C19.9719 19.1281 19.9166 19.2109 19.847 19.2806C19.7773 19.3502 19.6946 19.4055 19.6035 19.4432C19.5125 19.4809 19.4149 19.5003 19.3163 19.5003C19.2178 19.5003 19.1202 19.4809 19.0292 19.4432C18.9381 19.4055 18.8554 19.3502 18.7857 19.2806L12.5663 13.0602L6.34696 19.2806C6.20623 19.4213 6.01536 19.5003 5.81633 19.5003C5.61731 19.5003 5.42644 19.4213 5.28571 19.2806C5.14498 19.1398 5.06592 18.949 5.06592 18.7499C5.06592 18.5509 5.14498 18.36 5.28571 18.2193L11.506 11.9999L5.28571 5.78055C5.14498 5.63982 5.06592 5.44895 5.06592 5.24993C5.06592 5.05091 5.14498 4.86003 5.28571 4.7193C5.42644 4.57857 5.61731 4.49951 5.81633 4.49951C6.01536 4.49951 6.20623 4.57857 6.34696 4.7193L12.5663 10.9396L18.7857 4.7193C18.9264 4.57857 19.1173 4.49951 19.3163 4.49951C19.5154 4.49951 19.7062 4.57857 19.847 4.7193C19.9877 4.86003 20.0668 5.05091 20.0668 5.24993C20.0668 5.44895 19.9877 5.63982 19.847 5.78055L13.6266 11.9999L19.847 18.2193Z"
                          fill={colors.nightlyWoods500} />
                      </svg>
                    </button>
                  </div>
                </div>
              </Alert>}
            </div>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "24px" }}>
            {selectedClassCodes
              .filter(code => codesToConfig[code])
              .sort((codeA, codeB) => codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: 'base' }))
              .map((code) => {
                const config = codesToConfig[code];
                return (
                  <GroupCodeCard
                    activity={config.activity_type}
                    classroomId={config.classroom_id}
                    className={selectedClass?.name}
                    code={code}
                    setParentIsLoading={setIsLoading}
                    key={code}
                    sessionLength={config.session_length}
                    standardPrefix={config.standard_prefix}
                    standards={standardsData}
                    triggerRefresh={triggerRefresh}
                    triggerRefreshAll={triggerRefreshAll}
                    allCodesToConfig={codesToConfig}
                  />
                );
              })}
          </div>
        </>
      )}
      <div style={{
        alignSelf: "flex-end",
        bottom: "17px",
        cursor: "pointer",
        // Figma drop shadow for the Help icon (offset 0/4, blur 6, black 20%).
        filter: "drop-shadow(0px 4px 6px rgba(0, 0, 0, 0.2))",
        position: "fixed"
      }} onClick={handleQuestionClick}>
        <img src={Question} alt="Help" />
      </div>
      {isLoading && <div style={{ alignSelf: "center", position: "fixed", top: "50%" }}>
        <FontAwesomeIcon
          icon={faSpinner}
          spin
          size="5x"
        />
      </div>}
      {(error || accountError) &&
        <Alert style={{ overflowWrap: "break-word" }}
          className="center-display"
          dismissible
          onClose={() => setError(null)}
          variant="danger">{error || accountError?.detail || accountError?.message || "An unknown error occurred."}
        </Alert>
      }
      <EducatorGettingStartedModal
        isOpen={isEducatorGettingStartedModalOpen}
        setIsOpen={setIsEducatorGettingStartedModalOpen}
      />
      <ClassRenameModal
        isOpen={isClassRenameModalOpen}
        onClose={() => setIsClassRenameModalOpen(false)}
        selectedClass={selectedClass}
        onRename={handleClassRename}
      />
    </div>
  );
};
